import { FilterMode, FilterOption } from '../components/filter/Filter'
import {
  collectSelectableFilterValues,
  isFilterOptionSelectable,
} from './flattenFilterOptions'

export type ParentSelectionState = 'none' | 'some' | 'all'

const arraysEqualAsSet = <V>(a: V[], b: V[]): boolean => {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((v) => set.has(v))
}

export const isFilterValueSelected = <V>(value: V, draft: V[]): boolean =>
  draft.includes(value)

export const toggleFilterValue = <V>(
  value: V,
  draft: V[],
  mode: FilterMode
): V[] => {
  if (mode === 'single') {
    return draft.length === 1 && draft[0] === value ? [] : [value]
  }
  return draft.includes(value)
    ? draft.filter((v) => v !== value)
    : [...draft, value]
}

export const getFilterParentState = <V>(
  parent: FilterOption<V>,
  draft: V[]
): ParentSelectionState => {
  const childValues = collectSelectableFilterValues(parent).filter(
    (v) => v !== parent.value
  )
  if (childValues.length === 0) return 'none'
  const selected = childValues.filter((v) => draft.includes(v))
  if (selected.length === 0) return 'none'
  if (selected.length === childValues.length) return 'all'
  return 'some'
}

export const toggleFilterParent = <V>(
  parent: FilterOption<V>,
  draft: V[]
): V[] => {
  const state = getFilterParentState(parent, draft)
  const affected = collectSelectableFilterValues(parent)
  if (state === 'all') {
    const remove = new Set<V>(affected)
    return draft.filter((v) => !remove.has(v))
  }
  const next = new Set(draft)
  affected.forEach((v) => next.add(v))
  return Array.from(next)
}

export const handleFilterRowToggle = <V>(
  option: FilterOption<V>,
  draft: V[],
  mode: FilterMode
): V[] => {
  if (!isFilterOptionSelectable(option)) return draft
  if (option.children && option.children.length > 0 && mode === 'multi') {
    return toggleFilterParent(option, draft)
  }
  return toggleFilterValue(option.value, draft, mode)
}

export const countSelectedFilterValuesUnder = <V>(
  option: FilterOption<V>,
  draft: V[]
): number => {
  const all = collectSelectableFilterValues(option)
  if (all.length === 0) return 0
  const set = new Set(draft)
  return all.filter((v) => set.has(v)).length
}

export const filterArraysEqualUnordered = arraysEqualAsSet
